// src/UsefulLinks.js
// Страница «Полезные ссылки»: тематические блоки (ЕПС, МЭШ и т. п.)
// со ссылками. Учителя только читают, администратор правит прямо здесь.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from './api';
import QrCodeModal from './QrCodeModal';
import './UsefulLinks.css';

const ICONS = {
  link: (
    <>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
    </>
  ),
  mail: (
    <>
      <rect x="2" y="4" width="20" height="16" rx="2"></rect>
      <path d="m22 7-10 6L2 7"></path>
    </>
  ),
  school: (
    <>
      <path d="M22 10 12 5 2 10l10 5 10-5z"></path>
      <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
    </>
  ),
  book: (
    <>
      <path d="M2 4h6a4 4 0 0 1 4 4v13a3 3 0 0 0-3-3H2z"></path>
      <path d="M22 4h-6a4 4 0 0 0-4 4v13a3 3 0 0 1 3-3h7z"></path>
    </>
  ),
  docs: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      <polyline points="14 2 14 8 20 8"></polyline>
      <line x1="8" y1="13" x2="16" y2="13"></line>
      <line x1="8" y1="17" x2="13" y2="17"></line>
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2"></rect>
      <line x1="16" y1="2" x2="16" y2="6"></line>
      <line x1="8" y1="2" x2="8" y2="6"></line>
      <line x1="3" y1="10" x2="21" y2="10"></line>
    </>
  ),
  people: (
    <>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
      <circle cx="9" cy="7" r="4"></circle>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </>
  ),
  shield: (
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
  ),
  video: (
    <>
      <polygon points="23 7 16 12 23 17 23 7"></polygon>
      <rect x="1" y="5" width="15" height="14" rx="2"></rect>
    </>
  ),
  chart: (
    <>
      <line x1="18" y1="20" x2="18" y2="10"></line>
      <line x1="12" y1="20" x2="12" y2="4"></line>
      <line x1="6" y1="20" x2="6" y2="14"></line>
    </>
  ),
};

const ICON_TITLES = {
  link: 'Ссылка', mail: 'Почта', school: 'Школа', book: 'Книга', docs: 'Документы',
  calendar: 'Календарь', people: 'Люди', shield: 'Безопасность', video: 'Видео', chart: 'Отчёты',
};

const COLORS = [
  { id: 'green', title: 'Зелёный' },
  { id: 'blue', title: 'Синий' },
  { id: 'violet', title: 'Фиолетовый' },
  { id: 'orange', title: 'Оранжевый' },
  { id: 'red', title: 'Красный' },
  { id: 'teal', title: 'Бирюзовый' },
];

const EMPTY_GROUP = { title: '', description: '', icon: 'link', color: 'green' };
const EMPTY_LINK = { title: '', url: '', description: '' };

const Icon = ({ name, size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {ICONS[name] || ICONS.link}
  </svg>
);

const ButtonIcon = ({ children }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const EditIcon = () => (
  <ButtonIcon>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z"></path>
  </ButtonIcon>
);
const DeleteIcon = () => (
  <ButtonIcon>
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </ButtonIcon>
);
const UpIcon = () => <ButtonIcon><polyline points="18 15 12 9 6 15"></polyline></ButtonIcon>;
const DownIcon = () => <ButtonIcon><polyline points="6 9 12 15 18 9"></polyline></ButtonIcon>;
const PlusIcon = () => (
  <ButtonIcon>
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </ButtonIcon>
);

// Ошибки валидации приходят как {"url": ["Введите правильный URL."]} —
// показываем текст, а не JSON
const readError = (err) => {
  const message = (err && err.message) || 'Не удалось сохранить изменения';
  if (message === 'SESSION_EXPIRED') return 'Сессия истекла — войдите заново.';
  try {
    const parsed = JSON.parse(message);
    const [field, value] = Object.entries(parsed)[0];
    const text = Array.isArray(value) ? value[0] : String(value);
    return field === 'url' ? `Адрес: ${text}` : text;
  } catch (e) {
    return message;
  }
};

// «mail.mos.ru» без протокола сервер не примет — дописываем https://
const normalizeUrl = (value) => {
  const url = (value || '').trim();
  if (!url) return url;
  return /^[a-z][a-z0-9+.-]*:/i.test(url) ? url : `https://${url}`;
};

// Домен берём из самой строки: new URL() переводит кириллицу в punycode,
// и вместо «кабинет.рф» учитель увидел бы «xn--80atjc.xn--p1ai»
const hostOf = (url) => {
  const match = /^[a-z][a-z0-9+.-]*:\/\/(?:[^@/?#]*@)?([^/?#:]+)/i.exec(url || '');
  return match ? match[1].replace(/^www\./i, '') : url;
};

const matches = (query, ...values) =>
  values.some((value) => (value || '').toLowerCase().includes(query));

export default function UsefulLinks({ user }) {
  const isAdmin = !!(user && (user.role === 'admin' || user.is_admin || user.is_superuser));

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [query, setQuery] = useState('');
  const [actionError, setActionError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Открыта не больше чем одна форма: блока ('new' или id) либо ссылки
  const [groupFormId, setGroupFormId] = useState(null);
  const [groupDraft, setGroupDraft] = useState(EMPTY_GROUP);
  const [linkForm, setLinkForm] = useState(null); // { groupId, id | 'new' }
  const [linkDraft, setLinkDraft] = useState(EMPTY_LINK);
  // Ссылка, для которой открыт QR-код
  const [qrLink, setQrLink] = useState(null);

  // После изменений перечитываем список целиком, без индикатора загрузки:
  // порядок и состав всегда совпадают с сервером
  const refresh = useCallback(async () => {
    const data = await api.getLinkGroups();
    setGroups(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoadError(null);
        await refresh();
      } catch (err) {
        setLoadError(readError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [refresh]);

  const closeForms = () => {
    setGroupFormId(null);
    setLinkForm(null);
    setActionError(null);
  };

  const run = async (action) => {
    setSaving(true);
    setActionError(null);
    try {
      await action();
      await refresh();
      return true;
    } catch (err) {
      setActionError(readError(err));
      return false;
    } finally {
      setSaving(false);
    }
  };

  // ---------- блоки ----------

  const startCreateGroup = () => {
    closeForms();
    setGroupDraft(EMPTY_GROUP);
    setGroupFormId('new');
  };

  const startEditGroup = (group) => {
    closeForms();
    setGroupDraft({
      title: group.title || '',
      description: group.description || '',
      icon: group.icon || 'link',
      color: group.color || 'green',
    });
    setGroupFormId(group.id);
  };

  const saveGroup = async () => {
    if (!groupDraft.title.trim()) {
      setActionError('Укажите название блока.');
      return;
    }
    const ok = await run(() => (groupFormId === 'new'
      ? api.createLinkGroup(groupDraft)
      : api.updateLinkGroup(groupFormId, groupDraft)));
    if (ok) setGroupFormId(null);
  };

  const removeGroup = (group) => {
    const count = (group.links || []).length;
    const warning = count > 0
      ? `Удалить блок «${group.title}» вместе с ссылками (${count})?`
      : `Удалить блок «${group.title}»?`;
    if (!window.confirm(warning)) return;
    run(() => api.deleteLinkGroup(group.id));
  };

  // Порядок пересчитываем подряд для всего списка: так перестановка
  // работает, даже если после удалений в номерах есть пропуски или повторы
  const reorder = (items, index, delta, update) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    run(() => Promise.all(
      next
        .map((item, position) => (item.order === position ? null : update(item.id, { order: position })))
        .filter(Boolean)
    ));
  };

  // ---------- ссылки ----------

  const startCreateLink = (groupId) => {
    closeForms();
    setLinkDraft(EMPTY_LINK);
    setLinkForm({ groupId, id: 'new' });
  };

  const startEditLink = (groupId, link) => {
    closeForms();
    setLinkDraft({ title: link.title || '', url: link.url || '', description: link.description || '' });
    setLinkForm({ groupId, id: link.id });
  };

  const saveLink = async () => {
    const payload = { ...linkDraft, url: normalizeUrl(linkDraft.url) };
    if (!payload.title.trim() || !payload.url) {
      setActionError('Укажите название и адрес ссылки.');
      return;
    }
    const ok = await run(() => (linkForm.id === 'new'
      ? api.createLink({ ...payload, group: linkForm.groupId })
      : api.updateLink(linkForm.id, payload)));
    if (ok) setLinkForm(null);
  };

  const removeLink = (link) => {
    if (!window.confirm(`Удалить ссылку «${link.title}»?`)) return;
    run(() => api.deleteLink(link.id));
  };

  const onKeyDown = (e, save) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      closeForms();
    }
  };

  // ---------- отбор ----------

  const q = query.trim().toLowerCase();
  const visibleGroups = useMemo(() => groups
    .map((group) => {
      const links = group.links || [];
      if (!q || matches(q, group.title, group.description)) return { ...group, visibleLinks: links };
      return { ...group, visibleLinks: links.filter((l) => matches(q, l.title, l.description, l.url)) };
    })
    // Пустые блоки видит только администратор — чтобы было куда добавить ссылку
    .filter((group) => (q ? group.visibleLinks.length > 0 : (isAdmin || group.visibleLinks.length > 0))),
  [groups, q, isAdmin]);

  const renderGroupForm = () => (
    <div className="links-form">
      <div className="links-form-grid">
        <label className="links-field">
          <span>Название блока *</span>
          <input
            type="text"
            className="links-input"
            placeholder="Например, Единая почтовая система"
            value={groupDraft.title}
            autoFocus
            maxLength={150}
            onChange={(e) => setGroupDraft((prev) => ({ ...prev, title: e.target.value }))}
            onKeyDown={(e) => onKeyDown(e, saveGroup)}
          />
        </label>
        <label className="links-field">
          <span>Описание</span>
          <input
            type="text"
            className="links-input"
            placeholder="Коротко: что здесь собрано"
            value={groupDraft.description}
            maxLength={300}
            onChange={(e) => setGroupDraft((prev) => ({ ...prev, description: e.target.value }))}
            onKeyDown={(e) => onKeyDown(e, saveGroup)}
          />
        </label>
      </div>

      <div className="links-field">
        <span>Значок</span>
        <div className="links-picker" role="radiogroup" aria-label="Значок блока">
          {Object.keys(ICONS).map((name) => (
            <button
              key={name}
              type="button"
              role="radio"
              aria-checked={groupDraft.icon === name}
              aria-label={ICON_TITLES[name]}
              title={ICON_TITLES[name]}
              className={`links-picker-icon color-${groupDraft.color} ${groupDraft.icon === name ? 'selected' : ''}`}
              onClick={() => setGroupDraft((prev) => ({ ...prev, icon: name }))}
            >
              <Icon name={name} size={18} />
            </button>
          ))}
        </div>
      </div>

      <div className="links-field">
        <span>Цвет</span>
        <div className="links-picker" role="radiogroup" aria-label="Цвет блока">
          {COLORS.map((color) => (
            <button
              key={color.id}
              type="button"
              role="radio"
              aria-checked={groupDraft.color === color.id}
              aria-label={color.title}
              title={color.title}
              className={`links-picker-color color-${color.id} ${groupDraft.color === color.id ? 'selected' : ''}`}
              onClick={() => setGroupDraft((prev) => ({ ...prev, color: color.id }))}
            />
          ))}
        </div>
      </div>

      <div className="links-form-actions">
        <button type="button" className="links-button ghost" onClick={() => setGroupFormId(null)} disabled={saving}>
          Отмена
        </button>
        <button type="button" className="links-button" onClick={saveGroup} disabled={saving}>
          {saving ? 'Сохранение...' : (groupFormId === 'new' ? 'Создать блок' : 'Сохранить')}
        </button>
      </div>
    </div>
  );

  const renderLinkForm = () => (
    <div className="links-form links-form-link">
      <div className="links-form-grid">
        <label className="links-field">
          <span>Название *</span>
          <input
            type="text"
            className="links-input"
            placeholder="Например, Вход в почту"
            value={linkDraft.title}
            autoFocus
            maxLength={200}
            onChange={(e) => setLinkDraft((prev) => ({ ...prev, title: e.target.value }))}
            onKeyDown={(e) => onKeyDown(e, saveLink)}
          />
        </label>
        <label className="links-field">
          <span>Адрес *</span>
          <input
            type="text"
            inputMode="url"
            className="links-input"
            placeholder="https://..."
            value={linkDraft.url}
            maxLength={500}
            onChange={(e) => setLinkDraft((prev) => ({ ...prev, url: e.target.value }))}
            onKeyDown={(e) => onKeyDown(e, saveLink)}
          />
        </label>
      </div>
      <label className="links-field">
        <span>Описание</span>
        <input
          type="text"
          className="links-input"
          placeholder="Необязательно: подсказка, зачем эта ссылка"
          value={linkDraft.description}
          maxLength={300}
          onChange={(e) => setLinkDraft((prev) => ({ ...prev, description: e.target.value }))}
          onKeyDown={(e) => onKeyDown(e, saveLink)}
        />
      </label>
      <div className="links-form-actions">
        <button type="button" className="links-button ghost" onClick={() => setLinkForm(null)} disabled={saving}>
          Отмена
        </button>
        <button type="button" className="links-button" onClick={saveLink} disabled={saving}>
          {saving ? 'Сохранение...' : (linkForm && linkForm.id === 'new' ? 'Добавить' : 'Сохранить')}
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="links-state">
        <div className="loading-spinner"></div>
        <p>Загружаем ссылки...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="links-state">
        <p>Не удалось загрузить ссылки: {loadError}</p>
      </div>
    );
  }

  const reorderDisabled = saving || !!q;

  return (
    <div className="links">
      {qrLink && (
        <QrCodeModal title={qrLink.title} url={qrLink.url} onClose={() => setQrLink(null)} />
      )}

      <header className="links-header">
        <div className="links-heading">
          <h1 className="links-title">Полезные ссылки</h1>
          <p className="links-subtitle">Сервисы и ресурсы, которые нужны в работе</p>
        </div>

        <div className="links-tools">
          {groups.length > 0 && (
            <div className="links-search">
              <svg className="links-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                className="links-search-input"
                placeholder="Найти ссылку..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Поиск по ссылкам"
              />
              {query && (
                <button type="button" className="links-search-clear" onClick={() => setQuery('')} aria-label="Очистить поиск">
                  <ButtonIcon>
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </ButtonIcon>
                </button>
              )}
            </div>
          )}

          {isAdmin && (
            <button type="button" className="links-add-button" onClick={startCreateGroup}>
              <PlusIcon />
              Добавить блок
            </button>
          )}
        </div>
      </header>

      {actionError && <div className="links-error" role="alert">{actionError}</div>}

      {isAdmin && groupFormId === 'new' && (
        <section className={`links-group color-${groupDraft.color} links-group-new`}>
          <h2 className="links-form-title">Новый блок</h2>
          {renderGroupForm()}
        </section>
      )}

      {visibleGroups.length === 0 ? (
        groupFormId !== 'new' && (
          <div className="links-empty">
            <div className="links-empty-icon"><Icon name={q ? 'link' : 'book'} size={28} /></div>
            <h3>{q ? 'Ничего не найдено' : 'Ссылок пока нет'}</h3>
            <p>
              {q
                ? 'Попробуйте другое слово — название сервиса или часть адреса.'
                : (isAdmin
                  ? 'Создайте первый блок — например, «Единая почтовая система» или «МЭШ» — и добавьте в него ссылки.'
                  : 'Администратор ещё не добавил ссылки.')}
            </p>
            {isAdmin && !q && (
              <button type="button" className="links-add-button" onClick={startCreateGroup}>
                <PlusIcon />
                Добавить блок
              </button>
            )}
          </div>
        )
      ) : (
        <div className="links-grid">
          {visibleGroups.map((group) => {
            const groupIndex = groups.findIndex((g) => g.id === group.id);
            const allLinks = group.links || [];
            return (
              <section className={`links-group color-${groupFormId === group.id ? groupDraft.color : group.color}`} key={group.id}>
                {groupFormId === group.id ? (
                  <>
                    <h2 className="links-form-title">Редактирование блока</h2>
                    {renderGroupForm()}
                  </>
                ) : (
                  <div className="links-group-header">
                    <span className="links-group-badge"><Icon name={group.icon} /></span>
                    <div className="links-group-heading">
                      <h2 className="links-group-title">{group.title}</h2>
                      {group.description && <p className="links-group-description">{group.description}</p>}
                    </div>
                    {isAdmin && (
                      <div className="links-controls">
                        <button type="button" className="links-icon-button" onClick={() => reorder(groups, groupIndex, -1, api.updateLinkGroup)}
                          disabled={reorderDisabled || groupIndex === 0} title="Выше" aria-label={`Переместить блок «${group.title}» выше`}>
                          <UpIcon />
                        </button>
                        <button type="button" className="links-icon-button" onClick={() => reorder(groups, groupIndex, 1, api.updateLinkGroup)}
                          disabled={reorderDisabled || groupIndex === groups.length - 1} title="Ниже" aria-label={`Переместить блок «${group.title}» ниже`}>
                          <DownIcon />
                        </button>
                        <button type="button" className="links-icon-button" onClick={() => startEditGroup(group)}
                          disabled={saving} title="Изменить блок" aria-label={`Изменить блок «${group.title}»`}>
                          <EditIcon />
                        </button>
                        <button type="button" className="links-icon-button danger" onClick={() => removeGroup(group)}
                          disabled={saving} title="Удалить блок" aria-label={`Удалить блок «${group.title}»`}>
                          <DeleteIcon />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {group.visibleLinks.length > 0 && (
                  <ul className="links-list">
                    {group.visibleLinks.map((link) => {
                      const linkIndex = allLinks.findIndex((l) => l.id === link.id);
                      if (linkForm && linkForm.id === link.id) {
                        return <li key={link.id} className="links-list-form">{renderLinkForm()}</li>;
                      }
                      return (
                        <li key={link.id} className="links-row">
                          <a className="links-item" href={link.url} target="_blank" rel="noopener noreferrer">
                            <span className="links-item-text">
                              <span className="links-item-title">{link.title}</span>
                              {link.description && <span className="links-item-description">{link.description}</span>}
                              <span className="links-item-host">{hostOf(link.url)}</span>
                            </span>
                            <span className="links-item-arrow" aria-hidden="true">
                              <ButtonIcon>
                                <path d="M7 17 17 7"></path>
                                <polyline points="7 7 17 7 17 17"></polyline>
                              </ButtonIcon>
                            </span>
                          </a>
                          <button
                            type="button"
                            className="links-qr-button"
                            onClick={() => setQrLink(link)}
                            title="QR-код"
                            aria-label={`QR-код для «${link.title}»`}
                          >
                            <ButtonIcon>
                              <rect x="3" y="3" width="7" height="7" rx="1"></rect>
                              <rect x="14" y="3" width="7" height="7" rx="1"></rect>
                              <rect x="3" y="14" width="7" height="7" rx="1"></rect>
                              <path d="M14 14h3v3h-3z"></path>
                              <path d="M20 14v.01"></path>
                              <path d="M14 20h.01"></path>
                              <path d="M17 17h4v4h-4"></path>
                            </ButtonIcon>
                          </button>
                          {isAdmin && (
                            <div className="links-controls links-row-controls">
                              <button type="button" className="links-icon-button" onClick={() => reorder(allLinks, linkIndex, -1, api.updateLink)}
                                disabled={reorderDisabled || linkIndex === 0} title="Выше" aria-label={`Переместить «${link.title}» выше`}>
                                <UpIcon />
                              </button>
                              <button type="button" className="links-icon-button" onClick={() => reorder(allLinks, linkIndex, 1, api.updateLink)}
                                disabled={reorderDisabled || linkIndex === allLinks.length - 1} title="Ниже" aria-label={`Переместить «${link.title}» ниже`}>
                                <DownIcon />
                              </button>
                              <button type="button" className="links-icon-button" onClick={() => startEditLink(group.id, link)}
                                disabled={saving} title="Изменить" aria-label={`Изменить «${link.title}»`}>
                                <EditIcon />
                              </button>
                              <button type="button" className="links-icon-button danger" onClick={() => removeLink(link)}
                                disabled={saving} title="Удалить" aria-label={`Удалить «${link.title}»`}>
                                <DeleteIcon />
                              </button>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}

                {isAdmin && allLinks.length === 0 && !(linkForm && linkForm.groupId === group.id) && (
                  <p className="links-group-empty">В блоке пока нет ссылок</p>
                )}

                {isAdmin && linkForm && linkForm.groupId === group.id && linkForm.id === 'new' && renderLinkForm()}

                {isAdmin && !q && !(linkForm && linkForm.groupId === group.id && linkForm.id === 'new') && groupFormId !== group.id && (
                  <button type="button" className="links-add-link" onClick={() => startCreateLink(group.id)} disabled={saving}>
                    <PlusIcon />
                    Добавить ссылку
                  </button>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
